import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { BackButton, PrimaryButton } from 'components/forms/atoms/FormButtons';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { GLASS_BACKGROUND, GLASS_BLUR, NAVY, RADIUS_CARD, RADIUS_ITEM, SHADOW, SURFACE_TINT } from 'styles/tokens';

type TagKind = 'if_exists' | 'if_not_exists' | 'if_compare' | 'client_attr' | 'working_hours' | 'out_of_working_hours';

interface TagKindOption {
  value: TagKind;
  label: string;
  helper: string;
}

const TAG_KINDS: TagKindOption[] = [
  { value: 'if_exists', label: 'Si existe atributo', helper: 'Incluye el texto solo si el atributo tiene valor.' },
  {
    value: 'if_not_exists',
    label: 'Si no existe atributo',
    helper: 'Incluye el texto solo si el atributo está vacío, es falso o cero.',
  },
  { value: 'if_compare', label: 'Comparar atributo', helper: 'Incluye el texto si la comparación se cumple.' },
  {
    value: 'client_attr',
    label: 'Atributo del cliente',
    helper: 'Inserta el valor de un atributo del cliente directamente.',
  },
  {
    value: 'working_hours',
    label: 'Horario laboral',
    helper: 'Incluye el texto solo si la consulta ocurre en horario laboral.',
  },
  {
    value: 'out_of_working_hours',
    label: 'Fuera de horario laboral',
    helper: 'Incluye el texto solo si la consulta ocurre fuera del horario laboral.',
  },
];

type AttrType = 'string' | 'number' | 'boolean' | 'date' | 'array';

interface AttributeOption {
  value: string;
  label: string;
  type: AttrType;
  /** Spanish phrase used in natural-language descriptions (e.g. "el RUT"). */
  phrase: string;
  /** When true, the attribute is only valid in compare-style tags (e.g. `now`). */
  compareOnly?: boolean;
  /** When true, the attribute can't be used in compare-style tags */
  notForCompare?: boolean;
}

const ATTRIBUTES: AttributeOption[] = [
  { value: 'rut', label: 'RUT', type: 'string', phrase: 'el RUT' },
  { value: 'email', label: 'email', type: 'string', phrase: 'el email' },
  { value: 'phone', label: 'teléfono', type: 'string', phrase: 'el teléfono' },
  { value: 'firstName', label: 'nombre', type: 'string', phrase: 'el nombre' },
  { value: 'lastName', label: 'primer apellido', type: 'string', phrase: 'el primer apellido' },
  { value: 'motherLastName', label: 'segundo apellido', type: 'string', phrase: 'el segundo apellido' },
  { value: 'dateOfBirth', label: 'fecha de nacimiento', type: 'string', phrase: 'la fecha de nacimiento' },
  {
    value: 'lastMessageDate',
    label: 'fecha del último mensaje',
    type: 'date',
    phrase: 'la fecha del último mensaje',
    notForCompare: true,
  },
  { value: 'messagesCount', label: 'cantidad de mensajes', type: 'number', phrase: 'la cantidad de mensajes' },
  {
    value: 'clientMessagesCount',
    label: 'mensajes enviados por el cliente',
    type: 'number',
    phrase: 'la cantidad de mensajes enviados por el cliente',
  },
  {
    value: 'followUpsSent',
    label: 'follow-ups enviados',
    type: 'number',
    phrase: 'la cantidad de follow-ups enviados',
  },
  {
    value: 'disabledAutomaticResponses',
    label: 'respuestas automáticas desactivadas',
    type: 'boolean',
    phrase: 'el flag de respuestas automáticas desactivadas',
  },
  { value: 'source', label: 'origen', type: 'string', phrase: 'el origen' },
  { value: 'status', label: 'estado', type: 'string', phrase: 'el estado' },
  { value: 'topics', label: 'topics', type: 'array', phrase: 'los topics' },
  {
    value: 'now',
    label: 'fecha/hora actual (now)',
    type: 'date',
    phrase: 'la fecha/hora actual',
    compareOnly: true,
  },
];

const findAttributeByValue = (v: string): AttributeOption | undefined => ATTRIBUTES.find((a) => a.value === v);
const findAttributeByLabel = (l: string): AttributeOption | undefined =>
  ATTRIBUTES.find((a) => a.label.toLowerCase() === l.toLowerCase());

const OPERATORS: { value: string; label: string }[] = [
  { value: '=', label: 'es igual a' },
  { value: '!=', label: 'es distinto de' },
  { value: '>', label: 'es mayor que' },
  { value: '<', label: 'es menor que' },
  { value: '>=', label: 'es mayor o igual a' },
  { value: '<=', label: 'es menor o igual a' },
  { value: 'startsWith', label: 'empieza con' },
  { value: 'endsWith', label: 'termina con' },
  { value: 'contains', label: 'contiene' },
  { value: '!contains', label: 'no contiene' },
  { value: 'in', label: 'está en lista (coma)' },
];

// Operators the backend can actually evaluate for each value type.
// String/Date string ops vs numeric ops are mutually exclusive on the backend, so we
// gate them in the UI to prevent runtime "wrong type for operator" errors.
const OPERATORS_BY_TYPE: Record<AttrType, Set<string>> = {
  string: new Set(['=', '!=', 'startsWith', 'endsWith', 'contains', '!contains', 'in']),
  number: new Set(['=', '!=', '>', '<', '>=', '<=']),
  boolean: new Set(['=', '!=']),
  date: new Set(['=', '!=', '>', '<', '>=', '<=']),
  array: new Set(['=', '!=', 'contains', '!contains', 'in']),
};

const getValidOperatorsForAttribute = (attribute: string): { value: string; label: string }[] => {
  const meta = findAttributeByValue(attribute);
  // Unknown / free-text attribute: don't restrict — we can't infer the type.
  if (!meta) return OPERATORS;
  const allowed = OPERATORS_BY_TYPE[meta.type];
  return OPERATORS.filter((op) => allowed.has(op.value));
};

// Natural-language phrasing for the OR-helper sentence (slightly nicer than the dropdown labels).
const OPERATOR_PHRASE: Record<string, string> = {
  '=': 'es igual a',
  '!=': 'es distinto de',
  '>': 'es mayor que',
  '<': 'es menor que',
  '>=': 'es mayor o igual a',
  '<=': 'es menor o igual a',
  startsWith: 'empieza con',
  endsWith: 'termina con',
  contains: 'contiene',
  '!contains': 'no contiene',
  in: 'está en',
};

const SELF_CLOSING: TagKind[] = ['client_attr'];

const PROMPT_HELPER_COLOR = '#0072B2';

interface CompareCondition {
  attribute: string;
  operator: string;
  value: string;
}

const wrap = (open: string, close: string, inner: string): string => `${open}\n${inner}\n${close}`;

const serializeCompareConditions = (conditions: CompareCondition[]): string | null => {
  const parts = conditions
    .map((c) => {
      const a = c.attribute.trim();
      const v = c.value.trim();
      if (!a || !c.operator || !v) return null;
      return `${a}_${c.operator}_${v}`;
    })
    .filter((p): p is string => p !== null);
  if (parts.length === 0) return null;
  return parts.join('_OR_');
};

const describeCompareConditions = (conditions: CompareCondition[]): string | null => {
  const parts = conditions
    .map((c) => {
      const a = c.attribute.trim();
      const v = c.value.trim();
      if (!a || !c.operator || !v) return null;
      const meta = findAttributeByValue(a);
      const subject = meta?.phrase ?? a;
      const opPhrase = OPERATOR_PHRASE[c.operator] ?? c.operator;
      return `${subject} ${opPhrase} ${v}`;
    })
    .filter((p): p is string => p !== null);
  if (parts.length === 0) return null;
  return `Incluye el texto si ${parts.join(' o si ')}`;
};

const subjectFor = (attributeValue: string): string => {
  const a = attributeValue.trim();
  return findAttributeByValue(a)?.phrase ?? a;
};

const describeTag = (kind: TagKind, attribute: string, conditions: CompareCondition[], fallback: string): string => {
  switch (kind) {
    case 'if_exists': {
      if (!attribute.trim()) return fallback;
      return `Incluye el texto si ${subjectFor(attribute)} tiene valor`;
    }
    case 'if_not_exists': {
      if (!attribute.trim()) return fallback;
      return `Incluye el texto si ${subjectFor(attribute)} no tiene valor`;
    }
    case 'if_compare':
      return describeCompareConditions(conditions) ?? fallback;
    case 'client_attr': {
      if (!attribute.trim()) return fallback;
      return `Inserta ${subjectFor(attribute)} del cliente`;
    }
    default:
      return fallback;
  }
};

const buildTag = (kind: TagKind, attribute: string, conditions: CompareCondition[], innerText: string): string => {
  switch (kind) {
    case 'if_exists': {
      const a = attribute.trim();
      if (!a) return '';
      return wrap(`<IF:${a}>`, `</IF:${a}>`, innerText);
    }
    case 'if_not_exists': {
      const a = attribute.trim();
      if (!a) return '';
      return wrap(`<IF:!${a}>`, `</IF:!${a}>`, innerText);
    }
    case 'if_compare': {
      const expr = serializeCompareConditions(conditions);
      if (!expr) return '';
      return wrap(`<IF:${expr}>`, `</IF:${expr}>`, innerText);
    }
    case 'client_attr': {
      const a = attribute.trim();
      if (!a) return '';
      return `<CLIENT:${a}>`;
    }
    case 'working_hours':
      return wrap('<WORKING_HOURS>', '</WORKING_HOURS>', innerText);
    case 'out_of_working_hours':
      return wrap('<OUT_OF_WORKING_HOURS>', '</OUT_OF_WORKING_HOURS>', innerText);
    default:
      return '';
  }
};

interface PromptTagBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  onInsert: (tag: string) => void;
  initialInnerText?: string;
}

const fieldLabelSx = {
  fontFamily: 'Jost, sans-serif',
  fontWeight: 600,
  fontSize: '0.8rem',
  color: 'rgba(21, 24, 58, 0.7)',
  mb: 0.75,
  display: 'block',
};

const emptyCondition = (): CompareCondition => ({ attribute: 'rut', operator: '=', value: '' });

const PromptTagBuilderDialog: React.FC<PromptTagBuilderDialogProps> = ({
  open,
  onClose,
  onInsert,
  initialInnerText = '',
}) => {
  const [kind, setKind] = useState<TagKind>('if_exists');
  const [attribute, setAttribute] = useState<string>('rut');
  const [conditions, setConditions] = useState<CompareCondition[]>([emptyCondition()]);
  const [innerText, setInnerText] = useState<string>('');

  useEffect(() => {
    if (open) {
      setInnerText(initialInnerText);
      setConditions([emptyCondition()]);
    }
  }, [open, initialInnerText]);

  const tagKindInfo = useMemo(() => TAG_KINDS.find((t) => t.value === kind) ?? TAG_KINDS[0], [kind]);
  const needsSimpleAttribute = kind === 'if_exists' || kind === 'if_not_exists' || kind === 'client_attr';
  const needsConditions = kind === 'if_compare';
  const needsInnerText = !SELF_CLOSING.includes(kind);

  // `now` is only meaningful for compare-style tags; hide it elsewhere.
  const simpleAttributeOptions = useMemo(() => ATTRIBUTES.filter((a) => !a.compareOnly), []);
  const compareAttributeOptions = useMemo(() => ATTRIBUTES.filter((a) => !a.notForCompare), []);

  const preview = buildTag(kind, attribute, conditions, innerText || 'Texto');

  const updateCondition = (idx: number, patch: Partial<CompareCondition>) => {
    setConditions((prev) =>
      prev.map((c, i) => {
        if (i !== idx) return c;
        const next = { ...c, ...patch };
        // When the attribute changes, the previously selected operator may no longer
        // be valid for the new type (e.g. switching from a number to a string column
        // while `>` was selected). Reset to `=` in that case.
        if (patch.attribute !== undefined) {
          const allowed = getValidOperatorsForAttribute(next.attribute).map((o) => o.value);
          if (!allowed.includes(next.operator)) next.operator = '=';
        }
        return next;
      })
    );
  };

  const addCondition = () => setConditions((prev) => [...prev, emptyCondition()]);
  const removeCondition = (idx: number) => setConditions((prev) => prev.filter((_, i) => i !== idx));

  const handleInsert = () => {
    const tag = buildTag(kind, attribute, conditions, innerText);
    if (tag) {
      onInsert(tag);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: RADIUS_CARD,
            backgroundColor: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            boxShadow: SHADOW,
          },
        },
      }}
    >
      <DialogTitle sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, pt: 3, pb: 2, px: 3 }}>
        Agregar tag al prompt
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography component="label" sx={fieldLabelSx}>
            Tipo de tag
          </Typography>
          <Select
            fullWidth
            value={kind}
            onChange={(e) => setKind(e.target.value as TagKind)}
            sx={{ fontFamily: 'Jost, sans-serif' }}
          >
            {TAG_KINDS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {needsSimpleAttribute && (
          <Box>
            <Typography component="label" sx={fieldLabelSx}>
              Atributo
            </Typography>
            <Autocomplete<AttributeOption | string, false, false, true>
              freeSolo
              options={simpleAttributeOptions}
              value={findAttributeByValue(attribute) ?? attribute}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
              isOptionEqualToValue={(option, val) => {
                const optKey = typeof option === 'string' ? option : option.value;
                const valKey = typeof val === 'string' ? val : val.value;
                return optKey === valKey;
              }}
              onChange={(_, newValue) => {
                if (newValue == null) {
                  setAttribute('');
                  return;
                }
                if (typeof newValue === 'string') {
                  setAttribute(findAttributeByLabel(newValue)?.value ?? newValue);
                  return;
                }
                setAttribute(newValue.value);
              }}
              onInputChange={(_, newInput, reason) => {
                if (reason === 'input') {
                  setAttribute(findAttributeByLabel(newInput)?.value ?? newInput);
                }
              }}
              renderInput={(params) => <TextField {...params} placeholder="Ej: nombre, email, teléfono..." />}
            />
          </Box>
        )}

        {needsConditions && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography component="label" sx={fieldLabelSx}>
                Condiciones (unidas con OR)
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '0.8rem',
                  color: 'rgba(21, 24, 58, 0.6)',
                }}
              >
                El texto aparecerá cuando al menos una de las condiciones se cumpla.
              </Typography>
            </Box>
            {conditions.map((cond, idx) => {
              const operatorOptions = getValidOperatorsForAttribute(cond.attribute);
              const isNow = cond.attribute === 'now';
              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 1.5,
                    bgcolor: SURFACE_TINT,
                    borderRadius: RADIUS_ITEM,
                  }}
                >
                  {idx > 0 && (
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        color: PROMPT_HELPER_COLOR,
                        letterSpacing: '0.08em',
                      }}
                    >
                      OR
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Autocomplete<AttributeOption | string, false, false, true>
                      sx={{ flex: 1, minWidth: 0 }}
                      freeSolo
                      options={compareAttributeOptions}
                      value={findAttributeByValue(cond.attribute) ?? cond.attribute}
                      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                      isOptionEqualToValue={(option, val) => {
                        const optKey = typeof option === 'string' ? option : option.value;
                        const valKey = typeof val === 'string' ? val : val.value;
                        return optKey === valKey;
                      }}
                      onChange={(_, newValue) => {
                        if (newValue == null) {
                          updateCondition(idx, { attribute: '' });
                          return;
                        }
                        if (typeof newValue === 'string') {
                          updateCondition(idx, {
                            attribute: findAttributeByLabel(newValue)?.value ?? newValue,
                          });
                          return;
                        }
                        updateCondition(idx, { attribute: newValue.value });
                      }}
                      onInputChange={(_, newInput, reason) => {
                        if (reason === 'input') {
                          updateCondition(idx, {
                            attribute: findAttributeByLabel(newInput)?.value ?? newInput,
                          });
                        }
                      }}
                      renderInput={(params) => <TextField {...params} placeholder="Atributo" />}
                    />
                    {conditions.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeCondition(idx)}
                        aria-label="Eliminar condición"
                        sx={{ mt: 0.5 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Select
                      value={cond.operator}
                      onChange={(e) => updateCondition(idx, { operator: e.target.value })}
                      sx={{ minWidth: 180, fontFamily: 'Jost, sans-serif' }}
                    >
                      {operatorOptions.map((op) => (
                        <MenuItem key={op.value} value={op.value}>
                          {op.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <TextField
                      sx={{ flex: 1 }}
                      value={cond.value}
                      onChange={(e) => updateCondition(idx, { value: e.target.value })}
                      placeholder={
                        isNow
                          ? 'Ej: 2026-01-01 o 2026-01-01T15:30:00'
                          : cond.operator === 'in'
                            ? 'Ej: CHILENA,PERUANA'
                            : 'Ej: CHILENA, 0, true...'
                      }
                    />
                  </Box>
                </Box>
              );
            })}
            <Button
              onClick={addCondition}
              startIcon={<AddIcon />}
              sx={{
                alignSelf: 'flex-start',
                fontFamily: 'Jost, sans-serif',
                textTransform: 'none',
                color: PROMPT_HELPER_COLOR,
              }}
            >
              Agregar otra condición (OR)
            </Button>
          </Box>
        )}

        {needsInnerText && (
          <Box>
            <Typography component="label" sx={fieldLabelSx}>
              Texto interior
            </Typography>
            <TextField
              value={innerText}
              onChange={(e) => setInnerText(e.target.value)}
              placeholder="Texto que se insertará entre las etiquetas"
              multiline
              minRows={2}
              fullWidth
            />
          </Box>
        )}

        <Box sx={{ mt: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '0.95rem',
              color: PROMPT_HELPER_COLOR,
              mb: 1,
            }}
          >
            {describeTag(kind, attribute, conditions, tagKindInfo.helper)}
          </Typography>
          <Box
            sx={{
              p: 1.5,
              bgcolor: SURFACE_TINT,
              borderRadius: RADIUS_ITEM,
              border: `2px solid ${PROMPT_HELPER_COLOR}`,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              color: NAVY,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {preview || <span style={{ color: 'rgba(21, 24, 58, 0.5)' }}>Vista previa…</span>}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <BackButton onClick={onClose}>Cancelar</BackButton>
        <PrimaryButton onClick={handleInsert} disabled={!preview}>
          Insertar
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default PromptTagBuilderDialog;
